/**
 * PAN types, shared by the customer and admin apps.
 *
 * Mirrors the backend's app/constants/pan_types.py: the 4th character of a PAN
 * identifies the kind of entity it belongs to, and services declare which of
 * those types they are offered to.
 */

export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

/**
 * The closed set of PAN entity types, mirroring the backend's PanType enum.
 * `PanType` is a union rather than a TS enum so plain strings from the API
 * assign to it without casting.
 */
export const PAN_TYPES = [
  'individual',
  'huf',
  'firm',
  'llp',
  'company',
  'aop',
  'trust',
  'boi',
  'local_authority',
  'artificial_juridical',
  'government',
] as const;

export type PanType = (typeof PAN_TYPES)[number];

export function isPanType(value: string): value is PanType {
  return (PAN_TYPES as readonly string[]).includes(value);
}

/** 4th PAN character -> entity type. */
export const PAN_TYPE_MAP: Record<string, PanType> = {
  P: 'individual',
  H: 'huf',
  F: 'firm',
  E: 'llp',
  C: 'company',
  A: 'aop',
  T: 'trust',
  B: 'boi',
  L: 'local_authority',
  J: 'artificial_juridical',
  G: 'government',
};

export const PAN_TYPE_LABELS: Record<PanType, string> = {
  individual: 'Individual',
  huf: 'HUF',
  firm: 'Firm',
  llp: 'LLP',
  company: 'Company',
  aop: 'AOP',
  trust: 'Trust',
  boi: 'BOI',
  local_authority: 'Local Authority',
  artificial_juridical: 'Artificial Juridical Person',
  government: 'Government',
};

/**
 * One hue per PAN type so a customer can tell their PANs apart at a glance.
 * Plain hsl() strings, matching the app's inline-style colour convention.
 */
export const PAN_TYPE_COLORS: Record<PanType, string> = {
  individual: 'hsl(176 72% 30%)',
  huf: 'hsl(33 90% 45%)',
  firm: 'hsl(262 60% 55%)',
  llp: 'hsl(291 55% 48%)',
  company: 'hsl(212 80% 45%)',
  aop: 'hsl(340 65% 50%)',
  trust: 'hsl(150 55% 35%)',
  boi: 'hsl(20 75% 48%)',
  local_authority: 'hsl(190 65% 38%)',
  artificial_juridical: 'hsl(280 45% 50%)',
  government: 'hsl(0 0% 40%)',
};

export function panTypeLabel(type: string | null | undefined): string {
  if (!type) return '';
  return PAN_TYPE_LABELS[type as PanType] ?? type;
}

export function panTypeColor(type: string | null | undefined): string {
  if (!type) return 'hsl(var(--muted-foreground))';
  return PAN_TYPE_COLORS[type as PanType] ?? 'hsl(var(--muted-foreground))';
}

/** Trims and uppercases. Does not validate format. */
export function normalizePan(value: string): string {
  return value.trim().toUpperCase();
}

export function isValidPanFormat(value: string): boolean {
  return PAN_REGEX.test(normalizePan(value));
}

/** The entity type a PAN belongs to, or null when it is not a valid PAN. */
export function derivePanType(value: string): PanType | null {
  const normalized = normalizePan(value);
  if (!PAN_REGEX.test(normalized)) return null;
  return PAN_TYPE_MAP[normalized[3]!] ?? null;
}

/**
 * Compact "for whom" summary, e.g. "All", "HUF", "Individual, HUF +2".
 * An empty list means the service is offered to nobody.
 */
export function formatPanTypes(types: string[] | null | undefined, max = 3): string {
  if (!types || types.length === 0) return '—';
  if (types.length >= PAN_TYPES.length) return 'All';

  const ordered = PAN_TYPES.filter((t) => types.includes(t));
  const shown = ordered.slice(0, max).map(panTypeLabel);
  const rest = ordered.length - shown.length;
  return rest > 0 ? `${shown.join(', ')} +${rest}` : shown.join(', ');
}

/** Comma-separated string <-> list, for the MultiSelect component's value. */
export function csvToList(csv: string): string[] {
  return csv ? csv.split(',').filter(Boolean) : [];
}

export function listToCsv(values: readonly string[]): string {
  return values.join(',');
}
